// Articles service - fetches from Supabase database
import { supabase } from "../config/supabase.js";
import { authService } from "./auth.js";
import {
  ARTICLE_LIMITS,
  generateSlugFromTitle,
  isValidImageUrl,
} from "./articleSlug.js";
import {
  categoriesFromDbArticle,
  formatCategoriesDisplay,
  normalizeCategories,
} from "./articleCategories.js";

export function formatArticleSaveError(error) {
  if (!error) return "Error saving article. Please try again.";

  const message = error.message || "";
  const code = error.code || "";

  if (code === "23505") {
    return "An article with this title already exists. Change the title slightly and try again.";
  }
  if (
    code === "42501" ||
    message.includes("row-level security policy")
  ) {
    return "Database permissions blocked this save. Run database/migrations/006_fix_articles_rls_policies.sql in the Supabase SQL Editor (see database/RLS_FIX_README.md).";
  }
  if (message.includes("value too long") || code === "22001") {
    return `A field exceeds the allowed length (title/slug max ${ARTICLE_LIMITS.title} characters).`;
  }
  if (message.includes("image_url") || message.includes("violates not-null")) {
    return "Featured image is required. Upload an image or paste a valid https:// URL.";
  }
  if (
    message.includes("category") ||
    message.includes("categories") ||
    message.includes("articles_category_check")
  ) {
    return "Please select at least one valid category.";
  }

  return message || "Error saving article. Please try again.";
}

async function resolveUniqueArticleId(title, excludeId = null) {
  const base = generateSlugFromTitle(title);

  for (let suffix = 1; suffix <= 1000; suffix += 1) {
    const tail = suffix === 1 ? "" : `-${suffix}`;
    const candidate =
      suffix === 1
        ? base
        : `${base
            .slice(0, ARTICLE_LIMITS.slug - tail.length)
            .replace(/-+$/g, "")}${tail}`;

    const { data, error } = await supabase
      .from("articles")
      .select("id")
      .eq("id", candidate)
      .maybeSingle();
    if (error) throw error;
    if (!data || (excludeId && data.id === excludeId)) return candidate;
  }

  throw new Error("Could not generate a unique article slug.");
}

// Format date from database to display format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

// Transform database article to component format
const transformArticle = (dbArticle) => {
  if (!dbArticle) return null;

  const categories = categoriesFromDbArticle(dbArticle);

  return {
    id: dbArticle.id || dbArticle.slug,
    title: dbArticle.title,
    excerpt: dbArticle.excerpt,
    categories,
    category: formatCategoriesDisplay(categories),
    author: dbArticle.author,
    date: formatDate(dbArticle.published_date),
    readTime: dbArticle.read_time || "",
    image: dbArticle.image_url,
    content: dbArticle.content,
    slug: dbArticle.slug || dbArticle.id,
    is_published: dbArticle.is_published ?? false,
    created_by: dbArticle.created_by || "",
    updated_by: dbArticle.updated_by || "",
  };
};

const getActorIdentifier = () => {
  const user = authService.getCurrentUser();
  return user?.email || null;
};

// Get all published articles
export const getAllArticles = async () => {
  try {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("is_published", true)
      .order("published_date", { ascending: false });

    if (error) throw error;

    // Transform to component format
    const articles = {};
    data.forEach((article) => {
      const transformed = transformArticle(article);
      if (transformed) {
        articles[transformed.id] = transformed;
      }
    });

    return articles;
  } catch (error) {
    console.error("Error fetching articles:", error);
    return {};
  }
};

// Get article by ID or slug
export const getArticleById = async (id) => {
  try {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .or(`id.eq.${id},slug.eq.${id}`)
      .eq("is_published", true)
      .single();

    if (error) throw error;
    return transformArticle(data);
  } catch (error) {
    console.error("Error fetching article:", error);
    return null;
  }
};

// Get article by ID or slug for admin (includes unpublished)
export const getArticleByIdAdmin = async (id) => {
  try {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .or(`id.eq.${id},slug.eq.${id}`)
      .single();

    if (error) throw error;
    return transformArticle(data);
  } catch (error) {
    console.error("Error fetching article for admin:", error);
    return null;
  }
};

// Get all articles (including unpublished) for admin
export const getAllArticlesAdmin = async () => {
  try {
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .order("published_date", { ascending: false });

    if (error) throw error;

    // Transform to component format
    return data.map((article) => transformArticle(article));
  } catch (error) {
    console.error("Error fetching articles for admin:", error);
    return [];
  }
};

// Create or update article
export const saveArticle = async (articleData) => {
  try {
    const trimmedTitle = String(articleData.title || "").trim();
    const trimmedImage = String(articleData.image || "").trim();

    if (trimmedTitle.length > ARTICLE_LIMITS.title) {
      throw new Error(
        `Title must be ${ARTICLE_LIMITS.title} characters or fewer.`,
      );
    }
    if (
      String(articleData.author || "").trim().length > ARTICLE_LIMITS.author
    ) {
      throw new Error(
        `Author must be ${ARTICLE_LIMITS.author} characters or fewer.`,
      );
    }
    if (!isValidImageUrl(trimmedImage)) {
      throw new Error(
        "Featured image must be a valid http(s) URL. Upload an image or paste a full URL.",
      );
    }
    // Calculate read time if not provided
    const calculateReadTime = (htmlContent) => {
      if (!htmlContent) return "";
      const text = htmlContent
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      const words = text ? text.split(" ").length : 0;
      const wordsPerMinute = 200;
      const minutes = Math.max(1, Math.round(words / wordsPerMinute));
      return `${minutes} min read`;
    };

    // Parse date string to Date object
    const parseDate = (dateString) => {
      if (!dateString) return new Date().toISOString().split("T")[0];
      // Handle formats like "Oct 24, 2023" or ISO date
      if (dateString.includes(",")) {
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      }
      return dateString;
    };

    const actor = getActorIdentifier();

    const { data: existing, error: existingError } = await supabase
      .from("articles")
      .select("id")
      .eq("id", articleData.id)
      .maybeSingle();

    if (existingError) throw existingError;

    const articleId = existing
      ? articleData.id
      : await resolveUniqueArticleId(trimmedTitle);

    const categories = normalizeCategories(articleData.categories);
    if (!categories.length) {
      throw new Error("Please select at least one category.");
    }

    const articlePayload = {
      id: articleId,
      slug: articleId,
      title: trimmedTitle,
      excerpt: String(articleData.excerpt || "").trim(),
      categories,
      author: String(articleData.author || "").trim(),
      published_date: parseDate(articleData.date),
      image_url: trimmedImage,
      content: articleData.content,
      read_time: articleData.readTime || calculateReadTime(articleData.content),
      is_published: articleData.is_published === true,
    };

    if (existing) {
      if (actor) {
        articlePayload.updated_by = actor;
      }

      const { error } = await supabase
        .from("articles")
        .update(articlePayload)
        .eq("id", articleId);

      if (error) throw error;
      return transformArticle(articlePayload);
    } else {
      if (actor) {
        articlePayload.created_by = actor;
        articlePayload.updated_by = actor;
      }

      const { error } = await supabase
        .from("articles")
        .insert(articlePayload);

      if (error) throw error;
      return transformArticle(articlePayload);
    }
  } catch (error) {
    console.error("Error saving article:", error);
    throw error;
  }
};

// Delete article
export const deleteArticle = async (articleId) => {
  try {
    const { error } = await supabase
      .from("articles")
      .delete()
      .eq("id", articleId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error deleting article:", error);
    throw error;
  }
};

// Get similar articles by shared categories
export const getSimilarArticles = async (categories, excludeId, limit = 3) => {
  try {
    const normalized = normalizeCategories(categories);
    if (!normalized.length) return [];

    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .eq("is_published", true)
      .neq("id", excludeId)
      .overlaps("categories", normalized)
      .limit(limit)
      .order("published_date", { ascending: false });

    if (error) throw error;

    let articles = data.map((article) => transformArticle(article));

    // If we don't have enough similar articles, fill with any other articles
    if (articles.length < limit) {
      const excludeIds = [excludeId, ...articles.map((a) => a.id)];
      const { data: otherData, error: otherError } = await supabase
        .from("articles")
        .select("*")
        .eq("is_published", true)
        .not("id", "in", `(${excludeIds.join(",")})`)
        .limit(limit - articles.length)
        .order("published_date", { ascending: false });

      if (!otherError && otherData) {
        articles = [
          ...articles,
          ...otherData.map((article) => transformArticle(article)),
        ];
      }
    }

    return articles;
  } catch (error) {
    console.error("Error fetching similar articles:", error);
    return [];
  }
};

// Legacy export for backward compatibility (returns empty object, components should use async functions)
export const articles = {};
